import { format } from 'date-fns';

import { imgUser } from '@/assets/images';
import type { FamilyEvent } from '@/features/guardian/family-events';
import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';

export type CalendarViewMode = 'calendar' | 'list';

export type CalendarPlayer = {
  /** Player id — the filter key, matched against `FamilyEvent.playerId`. */
  id: string;
  /** Full name, used for accessibility and detail copy. */
  name: string;
  /** Chip label — the player's first name only. */
  label: string;
  avatar: FamilyEvent['playerAvatar'];
};

/** Map the guardian's API players to the avatar chips shown on the Calendar screen. */
export function toCalendarPlayers(players: GuardianPlayer[]): CalendarPlayer[] {
  return players.map((player, index) => {
    const firstName = player.firstname?.trim() ?? '';
    const lastName = player.lastname?.trim() ?? '';
    const name = [firstName, lastName].filter(Boolean).join(' ') || `Player ${index + 1}`;

    return {
      id: player.id,
      name,
      label: firstName || name,
      avatar: player.avatarUrl ? { uri: player.avatarUrl } : imgUser,
    };
  });
}

/** Keep only the events belonging to `playerId`; a null filter keeps every player. */
export function filterEventsByPlayer(
  events: FamilyEvent[],
  playerId: string | null,
): FamilyEvent[] {
  if (!playerId) return events;
  return events.filter(event => event.playerId === playerId);
}

/**
 * Events on or after `today`, optionally filtered to a single player, sorted ascending.
 * BR-05: the List view shows ALL future events (no 10-day look-ahead cap).
 */
export function getFutureFamilyEvents(
  events: FamilyEvent[],
  today: Date,
  playerId: string | null,
): FamilyEvent[] {
  const todayKey = format(today, 'yyyy-MM-dd');

  return filterEventsByPlayer(events, playerId).filter(event => event.date >= todayKey);
}
